// src/forms/data_hub_forget_user.js
// Extracted from data_hub.js — forget user / clear JWT cookie response handling.

import { whitelist_server_person_object_using_outgoing_field_list } from '../helpers/get_process_user_data';
import {
    remove_organization_jwt_cache_entry,
    write_organization_jwt_cache_entry,
} from '../helpers/organization_jwt_local_storage_cache';
import { re_prefill_all_forms, clear_all_form_inputs, clear_jwt_cookie } from './shared_form_ux_functions';
import { cleanup_analytics_state } from '../analytics/state';
import { write_persistent_do_not_track_flag } from '../analytics/privacy';

/**
 * Handles the response from the clear JWT endpoint (both same-origin and cross-origin iframe).
 * Sets the new unknown_uuid JWT, resets user_data, re-prefills forms, dispatches event.
 * Same pattern as got_cors_user_data_cache for the GET user data flow.
 *
 * @param {Object} data_hub_instance - The data_hub class instance.
 * @param {Object} payload - { jwt_cookie: "eyJ...", user_data: { global_uuid, global_uuid_source, uuids } }
 */
export function got_clear_jwt_response(data_hub_instance, payload) {
    if (!payload || typeof payload !== 'object') {
        return data_hub_instance.error_function?.('Invalid payload for got_clear_jwt_response', null);
    }

    // Idempotent — forget_user already called cleanup before firing the clear request, but
    // calling again on the response keeps the disabled flag set if a late capture somehow
    // landed in between (e.g. analytics module finished its dynamic import after forget_user
    // ran but before the server response arrived).
    cleanup_analytics_state(data_hub_instance);

    if (payload.jwt_cookie && typeof payload.jwt_cookie === 'string') {
        data_hub_instance.jwt_cookie = payload.jwt_cookie;
        write_organization_jwt_cache_entry(data_hub_instance, payload.jwt_cookie);
    }

    if (payload.user_data && typeof payload.user_data === 'object') {
        const user_data_after_outgoing_whitelist =
            whitelist_server_person_object_using_outgoing_field_list(
                payload.user_data,
                data_hub_instance
            );
        data_hub_instance.user_data = user_data_after_outgoing_whitelist;
        window.data_hub_user_data = user_data_after_outgoing_whitelist;
    } else {
        data_hub_instance.user_data = {};
        window.data_hub_user_data = {};
    }

    // Re-prefill forms with the server's response (unknown_uuid user_data has no visible
    // form fields like email/name, so this effectively blanks prefilled inputs).
    // Respects data-user-modified: fields the user is actively typing in won't be touched.
    re_prefill_all_forms(data_hub_instance.user_data || {}, data_hub_instance.prefill_input_selector, data_hub_instance);

    document.dispatchEvent(new CustomEvent('data-hub-new-user-data', {
        detail: {
            user_data: data_hub_instance.user_data || {},
            prefill_input_selector: data_hub_instance.prefill_input_selector || null,
            data_hub_instance: data_hub_instance,
            target: data_hub_instance.target || document,
            source: 'forget_user'
        },
        bubbles: true,
        composed: true
    }));
}

/**
 * Clears the user's JWT cookie, partitioned cookies, and local user data.
 * Works cross-origin via iframe passthrough.
 * The remote clear endpoint resets the cookie to a fresh unknown_uuid JWT and returns it.
 * When the response comes back (async), got_clear_jwt_response() handles setting the new
 * jwt_cookie, user_data, re-prefilling forms, and dispatching events.
 *
 * @param {Object} data_hub_instance - The data_hub class instance.
 * @param {Object} options
 * @param {boolean} options.clear_remote_cookie - Hit the clear endpoint to reset server-side JWT cookie to unknown_uuid. Default true.
 * @param {boolean} options.clear_local_user_data - Immediately clear user_data and window.data_hub_user_data (will be re-set when response arrives). Default true.
 * @param {boolean} options.re_prefill_forms_after_clear - Immediately re-prefill forms to clear visible inputs (will re-prefill again when response arrives). Default true.
 * @param {boolean} options.set_persistent_do_not_track - Write the org-scoped persistent DNT flag to localStorage so analytics stay disabled across page reloads. Default true. Pass false when forget_user is being used purely to refresh identity (not to opt out of tracking).
 * @returns {void}
 */
export function forget_user(data_hub_instance, options = {}) {
    // Coerce to booleans so null, 'true', 0, etc. all behave correctly.
    // Destructuring defaults only apply for undefined, NOT for null —
    // e.g. forget_user({ clear_remote_cookie: null }) would give null, not true.
    const clear_remote_cookie = options?.clear_remote_cookie !== false;
    const clear_local_user_data = options?.clear_local_user_data !== false;
    const re_prefill_forms_after_clear = options?.re_prefill_forms_after_clear !== false;
    const set_persistent_do_not_track = options?.set_persistent_do_not_track !== false;

    // Immediately clear local state so UI feels instant
    if (clear_local_user_data) {
        data_hub_instance.user_data = {};
        window.data_hub_user_data = {};
        // Wipe the whole LS cache entry — both `jwt` and the wide `user_data` slot.
        // Otherwise the wide user_data field (written by previous form submits) would
        // survive forget and re-populate prefill on the next reload, leaking PII past
        // the user's intent. got_clear_jwt_response will re-seed the entry with the
        // server-issued unknown_uuid JWT when the clear endpoint responds.
        remove_organization_jwt_cache_entry(data_hub_instance?.organization_id);
    }

    // Persistent, org-scoped DNT. Written BEFORE cleanup_analytics_state so the localStorage
    // write completes even if cleanup somehow short-circuits later. Next page load reads this
    // flag in init_analytics_bootstrap and never starts the analytics session.
    if (set_persistent_do_not_track) {
        write_persistent_do_not_track_flag(data_hub_instance, true);
    }

    // Permanent disable. Runs cleanup callbacks (removes analytics auto-event listeners,
    // restores native pushState/replaceState, clears the periodic flush timer), purges in-
    // memory + localStorage retry queues, and flips analytics_state.captures_disabled so any
    // later capture / flush / persist_failed_events call short-circuits without touching
    // network or storage. v1 does not re-enable on a future consent grant — that requires
    // a page reload AND clearing the persistent DNT flag.
    cleanup_analytics_state(data_hub_instance);

    // Immediately blank all form inputs — sets text fields to '', unchecks checkboxes, etc.
    // Also removes data-user-modified so future prefills can write to these fields.
    // Note: re_prefill_all_forms({}) does NOT clear fields — it only sets values for matching
    // keys in user_data, and an empty object has no keys. clear_all_form_inputs actively wipes.
    if (re_prefill_forms_after_clear) {
        clear_all_form_inputs(data_hub_instance);
    }

    // Fire-and-forget: hit the clear endpoint. When the response comes back,
    // got_clear_jwt_response() will set the new unknown_uuid JWT + user_data,
    // re-prefill forms with the server response, and dispatch the event.
    if (clear_remote_cookie) {
        clear_jwt_cookie(data_hub_instance.forms_domain, data_hub_instance.error_function, data_hub_instance);
    }

    // Dispatch immediately so listeners can react to the clear right away.
    // got_clear_jwt_response() will dispatch again when the server responds
    // with the actual unknown_uuid user_data + jwt_cookie set.
    document.dispatchEvent(new CustomEvent('data-hub-new-user-data', {
        detail: {
            user_data: data_hub_instance.user_data || {},
            prefill_input_selector: data_hub_instance.prefill_input_selector || null,
            data_hub_instance: data_hub_instance,
            target: data_hub_instance.target || document,
            source: 'forget_user'
        },
        bubbles: true,
        composed: true
    }));
}
